import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import Cropper, { ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";
import classNames from "classnames";
import styles from "./style.module.scss";

// Extracted imports
import { VLCropperProps, VLCropperRef } from "./types";
import { CROP_BOX_SIZE_RATIO, MIN_CROP_SIZE } from "./constants";
import {
  getCurrentCropInOriginalCoordinates,
  extractClickPositionFromEvent,
  isClickWithoutDrag,
  calculateCropPosition,
  applyMinimumSizeConstraints,
  setupBoundingBoxCrop,
} from "./utils";
import { useCropperState } from "./hooks/useCropperState";
import { useCropperOperations } from "./hooks/useCropperOperations";

const VLCropper = forwardRef<VLCropperRef, VLCropperProps>(
  (
    { src, onCropChange, boundingBox, className, autoCrop, cropBoxSizeRatio },
    ref,
  ) => {
    const cropperRef = useRef<ReactCropperElement>(null);

    // Use extracted state management hook
    const {
      clickPosition,
      isFullCrop,
      hasInitialCrop,
      lastClickPositionRef,
      cropStartDataRef,
      setClickPosition,
      setIsFullCrop,
      setHasInitialCrop,
      updateClickPosition,
      storeCropStartState,
      resetState,
    } = useCropperState();

    // Use extracted operations hook
    const { resetCrop, getCropData, setupAutoCrop } = useCropperOperations({
      cropperRef,
      onCropChange,
      resetState,
      setClickPosition,
      setHasInitialCrop,
    });

    const onReady = () => {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) return;

      if (boundingBox) {
        const position = setupBoundingBoxCrop(cropper, boundingBox);
        setClickPosition(position);
      }
    };

    useImperativeHandle(ref, () => ({ resetCrop, getCropData }));

    // React to autoCrop prop changes
    useEffect(() => {
      const cropper = cropperRef.current?.cropper;
      if (!cropper || !autoCrop || hasInitialCrop) return;

      setupAutoCrop();
    }, [autoCrop, hasInitialCrop, setupAutoCrop]);

    const handleCropBoxClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const rawClickX = e.clientX - rect.left;
      const rawClickY = e.clientY - rect.top;

      const box = cropper.getCropBoxData();
      const container = cropper.getContainerData();

      // Convert to container coordinate system
      const containerOffsetX = (rect.width - container.width) / 2;
      const containerOffsetY = (rect.height - container.height) / 2;
      const clickX = rawClickX - containerOffsetX;
      const clickY = rawClickY - containerOffsetY;

      if (!box || box.width === 0 || box.height === 0) {
        // No crop exists - create new crop with MIN_CROP_SIZE at click position
        const width = MIN_CROP_SIZE;
        const height = MIN_CROP_SIZE;
        const left = Math.max(
          0,
          Math.min(clickX - width / 2, container.width - width),
        );
        const top = Math.max(
          0,
          Math.min(clickY - height / 2, container.height - height),
        );

        cropper.setCropBoxData({ width, height, left, top });
        cropper.crop(); // Show the crop box
        setClickPosition({ x: left + width / 2, y: top + height / 2 });

        const currentCrop = getCurrentCropInOriginalCoordinates(cropperRef);
        onCropChange?.(true, currentCrop || undefined);
      } else {
        // Crop exists - existing behavior (fullCrop / normalCrop)
        const insideBox =
          clickX >= box.left &&
          clickX <= box.left + box.width &&
          clickY >= box.top &&
          clickY <= box.top + box.height;

        if (!insideBox) return;

        if (!isFullCrop) {
          cropper.setCropBoxData({
            width: container.width,
            height: container.height,
            left: 0,
            top: 0,
          });
          setIsFullCrop(true);
        } else {
          cropper.setCropBoxData({
            width: MIN_CROP_SIZE,
            height: MIN_CROP_SIZE,
            left: Math.max(
              0,
              Math.min(
                clickPosition!.x - MIN_CROP_SIZE / 2,
                container.width - MIN_CROP_SIZE,
              ),
            ),
            top: Math.max(
              0,
              Math.min(
                clickPosition!.y - MIN_CROP_SIZE / 2,
                container.height - MIN_CROP_SIZE,
              ),
            ),
          });
          setIsFullCrop(false);
        }
      }
    };

    return (
      <Cropper
        movable={false}
        className={classNames(styles.cropper, className)}
        ref={cropperRef}
        src={src}
        style={{ maxWidth: "100%" }}
        autoCropArea={cropBoxSizeRatio || CROP_BOX_SIZE_RATIO}
        dragMode="crop"
        viewMode={1}
        guides={false}
        cropBoxResizable={true}
        cropBoxMovable={true}
        background={false}
        autoCrop={!!boundingBox || autoCrop}
        ready={onReady}
        onDoubleClick={handleCropBoxClick}
        cropstart={(e: any) => {
          // Store the initial crop state to compare later in cropend
          const cropper = cropperRef.current?.cropper;
          if (cropper) {
            const initialBox = cropper.getCropBoxData();
            storeCropStartState({
              left: initialBox.left || 0,
              top: initialBox.top || 0,
              width: initialBox.width || 0,
              height: initialBox.height || 0,
            });
          }

          // Capture click position from the original event
          const clickPos = extractClickPositionFromEvent(e, cropperRef);
          if (clickPos) {
            updateClickPosition(clickPos);
          }

          const currentCrop = getCurrentCropInOriginalCoordinates(cropperRef);
          onCropChange?.(true, currentCrop || undefined);
        }}
        cropend={() => {
          const cropper = cropperRef.current?.cropper;
          if (!cropper) return;

          const box = cropper.getCropBoxData();
          if (!box || box.width === 0 || box.height === 0) return;

          const wasClickWithoutDrag = isClickWithoutDrag(
            cropStartDataRef.current,
            box,
          );
          let { width, height, left, top } = box;

          if (wasClickWithoutDrag) {
            const canvas = cropper.getCanvasData();
            width = MIN_CROP_SIZE;
            height = MIN_CROP_SIZE;

            const clickPos = lastClickPositionRef.current;
            if (clickPos) {
              const position = calculateCropPosition(
                clickPos,
                canvas,
                width,
                height,
              );
              left = position.left;
              top = position.top;
            } else {
              // Fallback to center of canvas
              left = canvas.left + (canvas.width - width) / 2;
              top = canvas.top + (canvas.height - height) / 2;
            }
          } else {
            // Apply minimum size constraints to existing crops
            const constrained = applyMinimumSizeConstraints(box);
            width = constrained.width;
            height = constrained.height;
            left = constrained.left;
            top = constrained.top;
          }

          // For click without drag, ensure crop exists first if needed
          if (wasClickWithoutDrag && !hasInitialCrop) {
            cropper.crop();
          }

          // Set the crop box to the desired size and position
          cropper.setCropBoxData({ width, height, left, top });
          setClickPosition({ x: left + width / 2, y: top + height / 2 });

          const currentCrop = getCurrentCropInOriginalCoordinates(cropperRef);
          onCropChange?.(true, currentCrop || undefined);

          // Set hasInitialCrop to true after any successful crop operation
          if (!hasInitialCrop) {
            setHasInitialCrop(true);
          }
        }}
      />
    );
  },
);

export default VLCropper;
